// DayPartRow.tsx
import React from "react";
import DateInput from "./DateInput";
import styles from "./DayPartRow.module.css";
import { DayPartRowType, RecommendationStatus } from "./types";
import { DayPart } from "../../gql/types";
import DayPartIconButton from "./DayPartIconButton";
import ErrorUnit from "../input/ErrorUnit";

interface DayPartRowProps {
    id: number;
    date: string;
    selectedDayParts: DayPart[];
    singleSelection?: boolean;
    onRowChange: (id: number, field: keyof DayPartRowType, value: string | DayPart[]) => void;
    enableDateSelection?: boolean;
    readOnly?: boolean;
    noPastDate?: boolean;
    errorStr?: string | null;
}

const DayPartRow: React.FC<DayPartRowProps> = ({
    id,
    date,
    selectedDayParts,
    singleSelection,
    onRowChange,
    enableDateSelection = true,
    readOnly,
    noPastDate = false,
    errorStr,
}) => {
    // Toggle a given daypart
    const toggleDayPart = (dp: DayPart) => {
        if (readOnly) return;
        const already = selectedDayParts.includes(dp);
        const newSelection = already
            ? selectedDayParts.filter((x) => x !== dp)
            : [...selectedDayParts, dp];
        onRowChange(id, "selectedDayParts", newSelection);
    };

    return (
        <div className={styles.dayPartRowWrapper}>
            <div className={`
                ${styles.dayPartRowContainer}
                ${readOnly ? styles.readOnly : ''}
                ${errorStr && errorStr !== null ? styles.errorFocus : ''}
                 ${singleSelection && selectedDayParts.length > 0 ? styles.selectedRow : ''}
                `}>
                {enableDateSelection && (
                    <div className={styles.datePickerContainer}>
                        <DateInput
                            disabled={readOnly}
                            selected={singleSelection && selectedDayParts.length > 0}
                            date={date}
                            onDateChange={(newDate: string) => {
                                onRowChange(id, "date", newDate);
                            }}
                            noPastDate={noPastDate}
                        />
                    </div>
                )}
                <div className={styles.dayPartSelection}>
                    <DayPartIconButton
                        selected={selectedDayParts.includes(DayPart.Morning)}
                        onClick={() => toggleDayPart(DayPart.Morning)}
                        dayPart={DayPart.Morning}
                        recommendation={RecommendationStatus.Neutral}
                        readOnly={readOnly}
                        useTransparentStyling={
                            !readOnly &&
                            singleSelection &&
                            selectedDayParts.length > 0 &&
                            !selectedDayParts.includes(DayPart.Morning)}
                    />
                    <DayPartIconButton
                        selected={selectedDayParts.includes(DayPart.EarlyAfternoon)}
                        onClick={() => toggleDayPart(DayPart.EarlyAfternoon)}
                        dayPart={DayPart.EarlyAfternoon}
                        recommendation={RecommendationStatus.Neutral}
                        readOnly={readOnly}
                        useTransparentStyling={
                            !readOnly &&
                            singleSelection &&
                            selectedDayParts.length > 0 &&
                            !selectedDayParts.includes(DayPart.EarlyAfternoon)}
                    />
                    <DayPartIconButton
                        selected={selectedDayParts.includes(DayPart.LateAfternoon)}
                        onClick={() => toggleDayPart(DayPart.LateAfternoon)}
                        dayPart={DayPart.LateAfternoon}
                        recommendation={RecommendationStatus.Neutral}
                        readOnly={readOnly}
                        useTransparentStyling={
                            !readOnly &&
                            singleSelection &&
                            selectedDayParts.length > 0 &&
                            !selectedDayParts.includes(DayPart.LateAfternoon)}
                    />
                    <DayPartIconButton
                        selected={selectedDayParts.includes(DayPart.Evening)}
                        onClick={() => toggleDayPart(DayPart.Evening)}
                        dayPart={DayPart.Evening}
                        recommendation={RecommendationStatus.Neutral}
                        readOnly={readOnly}
                        useTransparentStyling={
                            !readOnly &&
                            singleSelection &&
                            selectedDayParts.length > 0 &&
                            !selectedDayParts.includes(DayPart.Evening)}
                    />
                </div>
            </div>
            <ErrorUnit isFocused={false} errorStr={errorStr ?? null} />
        </div>
    );
};

export default DayPartRow;
